import isEqual from 'lodash-es/isEqual';
import { createSelector } from 'reselect';
import { IState } from '..';
import { IOption } from '../../components/Select';
import { notUndefined } from '../../utils/objectUtils';
import { IRegion, ShoreMasterDepartment } from '../lookups';
import { getSid } from '../me';
import { WarehouseUserPermissionType } from '../warehousePermissions';
import { AccessLevel, IBasePermissions, IWarehousePermissions, PermissionTypes } from './authInterfaces';

export const getPermissions = createSelector(
  [(state: IState) => state.auth.permissions, (state: IState) => state.auth.itOverridePermissions],
  (basePermissions, itOverRides) => (itOverRides ? itOverRides : basePermissions)
);

export const isUser = createSelector(
  [getPermissions, (_: unknown, type: PermissionTypes | PermissionTypes[]) => type],
  (permissions, type): boolean => {
    let enabled = false;
    if (Array.isArray(type)) {
      // create an array of the requested access levels
      const perms = type.map<AccessLevel>(x => (permissions[x] ? permissions[x].accessLevel : AccessLevel.None));

      // check if some of them are not equal to none
      enabled = perms.some(x => x !== AccessLevel.None);
    } else if (permissions[type]) {
      enabled = permissions[type].accessLevel !== AccessLevel.None;
    }

    return enabled;
  }
);

export const isWriteUser = createSelector(
  [getPermissions, (_: unknown, type: PermissionTypes | PermissionTypes[]) => type],
  (permissions, type): boolean => {
    let enabled = false;
    if (permissions) {
      if (Array.isArray(type)) {
        const perms = type.map<AccessLevel>(x =>
          permissions[x] ? (permissions[x] as IBasePermissions).accessLevel : AccessLevel.None
        );

        enabled = perms.some(x => x !== (AccessLevel.ReadOnly || AccessLevel.None));
      } else if (permissions[type]) {
        enabled = (permissions[type] as IBasePermissions).accessLevel !== (AccessLevel.ReadOnly || AccessLevel.None);
      }
    }

    return enabled;
  }
);

export const isAdmin = createSelector(
  [getPermissions, (_: unknown, type: PermissionTypes | PermissionTypes[]) => type],
  (permissions, type) => {
    let enabled = false;
    if (permissions) {
      if (Array.isArray(type)) {
        // create an array of the requested access levels
        const perms = type.map<AccessLevel>(x =>
          permissions[x] ? (permissions[x] as IBasePermissions).accessLevel : AccessLevel.None
        );

        // check if some of them are not equal to none
        enabled = perms.every(x => x === AccessLevel.Admin);
      } else {
        enabled = (permissions[type] as IBasePermissions).accessLevel === AccessLevel.Admin;
      }
    }

    return enabled;
  }
);

export const isItOverriddenByType = createSelector(
  [
    (state: IState) => state.auth.permissions, // This doesn't use getPermissions because we are checking against it
    (state: IState) => state.auth.itOverridePermissions,
    (_: unknown, type: PermissionTypes) => type
  ],
  (basePermissions, itOverride, type): boolean => {
    if (itOverride) {
      return !isEqual(basePermissions[type], itOverride[type]);
    }
    return false;
  }
);

export const isItOverridden = createSelector(
  [(state: IState) => state.auth.itOverridePermissions],
  (itOverride): boolean => {
    return Boolean(itOverride);
  }
);

export const isUserByDepartment = createSelector(
  [getPermissions, (_: unknown, departmentId: ShoreMasterDepartment) => departmentId],
  (permissions, departmentId): boolean => {
    const permissionType = convertShoremasterDepartmentToPermissionType(departmentId);
    return permissions[permissionType].accessLevel !== AccessLevel.None;
  }
);

export const isAdminByDepartment = createSelector(
  [getPermissions, (_: unknown, departmentId: ShoreMasterDepartment) => departmentId],
  (permissions, departmentId) => {
    const permissionType = convertShoremasterDepartmentToPermissionType(departmentId);
    return permissions[permissionType].accessLevel === AccessLevel.Admin;
  }
);

export const isFullAccessOrAdmin = createSelector(
  [getPermissions, (_: unknown, type: PermissionTypes) => type],
  (permissions, type): boolean => {
    console.log('permissions', permissions)
    console.log('type', type)
    return (
      permissions[type].accessLevel === AccessLevel.Admin || permissions[type].accessLevel === AccessLevel.FullAccess
    );
  }
);

export const getDefaultSalesDistricts = createSelector(getPermissions, permissions => permissions.sales.salesDistricts);

export const getDefaultSalesDistrictOptions = createSelector(
  [getDefaultSalesDistricts, (state: IState) => state.lookups.salesDistricts.hash],
  (salesDistricts, hash): IOption[] => {
    return salesDistricts.map<IOption>(x => {
      const sd = hash[x];

      return sd
        ? { label: sd.salesDistrictId, secondaryLabel: sd.name, value: sd.salesDistrictId }
        : { label: x, secondaryLabel: x, value: x };
    });
  }
);

export const getSalesRegionIds = createSelector(getPermissions, permissions => permissions.sales.regions);

export const getSalesRegions = createSelector(
  [getSalesRegionIds, (state: IState) => state.lookups.regions.hash],
  (regionIds, hash) => regionIds.map(x => hash[x]).filter(x => x)
);

export const isRsmOfAnySalesDist = createSelector(getSalesRegions, regions => regions.length > 0);

export const isRsm = createSelector(
  [getSid, (state: IState) => state.lookups.regions.hash, (_: IState, salesDistrictId: string) => salesDistrictId],
  (sid, regionHash, salesDistrictId): boolean => {
    return Boolean(
      Object.values(regionHash).find((x: IRegion) => x.managerSid === sid && x.salesDistricts.includes(salesDistrictId))
    );
  }
);

export function isRsmOrSalesAdmin(state: IState, salesDistrictId: string): boolean {
  return isRsm(state, salesDistrictId) || isAdmin(state, 'sales');
}

export const getLogisticsUserWarehousePermissions = createSelector(
  getPermissions,
  permissions => permissions.logistics.warehouses
);

export const getLogisticsUserWarehouses = createSelector(getLogisticsUserWarehousePermissions, (warehouses): string[] =>
  warehouses.map(x => x.warehouseId)
);

export const getOperationsUserWarehousePermissions = createSelector(
  getPermissions,
  permissions => permissions.operations.warehouses
);

export const getOperationsUserWarehouses = createSelector(
  getOperationsUserWarehousePermissions,
  (warehouses): string[] => warehouses.map(x => x.warehouseId)
);

export const getIsWarehouseWriteUser = createSelector(
  [
    getPermissions,
    (_: IState, value: { departmentId: ShoreMasterDepartment; warehouseId: string[] | string }) => value
  ],
  (permissions, value) => {
    const permissionType = convertShoremasterDepartmentToPermissionType(value.departmentId);
    const warehouseIds = Array.isArray(value.warehouseId) ? value.warehouseId : [value.warehouseId];

    return (permissions[permissionType] as IWarehousePermissions).warehouses.some(
      // used the number 2 instead of the enum as createSelector hated it
      x => warehouseIds.includes(x.warehouseId) && x.permissionTypeId === 2
    );
  }
);

export const canEditWarehouseUsers = createSelector(
  [getPermissions, (_: IState, departmentId: ShoreMasterDepartment) => departmentId],
  (permissions, departmentId) => {
    const permissionType = convertShoremasterDepartmentToPermissionType(departmentId);
    const whPermissions = permissions[permissionType] as IWarehousePermissions;

    // if the user is an admin, or they manage more than 0 warehouse groups
    return whPermissions.accessLevel === AccessLevel.Admin || whPermissions.warehouseGroupIds.length > 0;
  }
);

export const getWarehouseAccessLevel = createSelector(
  [
    getPermissions,
    (state: IState) => state.lookups.warehouseGroups.items,
    (_: IState, values: { departmentId: ShoreMasterDepartment; originWarehouseIds: string[] }) => values
  ],
  (permissions, warehouseGroups, { departmentId, originWarehouseIds }): AccessLevel => {
    const permissionType = convertShoremasterDepartmentToPermissionType(departmentId);
    const whPermissions = permissions[permissionType] as IWarehousePermissions;
    const userWarehouseGroups = whPermissions.warehouseGroupIds
      .map(x => warehouseGroups.find(y => y.warehouseGroupId === x))
      .filter(notUndefined);

    if (
      // if the user is an admin
      whPermissions.accessLevel === AccessLevel.Admin ||
      // or they are a warehouse group manager for that warehouse
      userWarehouseGroups.some(x => x.warehouses.some(y => originWarehouseIds.includes(y)))
    ) {
      // they have admin access to the load
      return AccessLevel.Admin;
    }

    const groupedPermCount = originWarehouseIds.reduce<Record<WarehouseUserPermissionType, number>>(
      (hash, warehouse) => {
        //find the warehouse in the warehouse permissions
        const permType = whPermissions.warehouses.find(x => x.warehouseId === warehouse);
        if (permType) {
          hash[permType.permissionTypeId] += 1;
        }

        return hash;
      },
      {
        [WarehouseUserPermissionType.Read]: 0,
        [WarehouseUserPermissionType.Write]: 0
      }
    );

    if (groupedPermCount[WarehouseUserPermissionType.Write] > 0) {
      return AccessLevel.Write;
    } else if (groupedPermCount[WarehouseUserPermissionType.Read] > 0) {
      return AccessLevel.ReadOnly;
    }
    // if there are no originWarehouses (an empty load) return the users default access level
    else if (originWarehouseIds.length === 0) {
      return whPermissions.accessLevel;
    } else {
      return AccessLevel.None;
    }
  }
);

export const getAuthorizedWarehouseGroupIds = createSelector(
  [getPermissions, (_: unknown, departmentId: ShoreMasterDepartment) => departmentId],
  (permissions, departmentId): number[] => {
    const permissionType = convertShoremasterDepartmentToPermissionType(departmentId);
    return permissions &&
      permissions[permissionType] &&
      (permissions[permissionType] as IWarehousePermissions).warehouseGroupIds
      ? (permissions[permissionType] as IWarehousePermissions).warehouseGroupIds
      : [];
  }
);

const convertShoremasterDepartmentToPermissionType = (departmentId: ShoreMasterDepartment): PermissionTypes => {
  switch (departmentId) {
    case ShoreMasterDepartment.Finance:
      return 'finance';
    case ShoreMasterDepartment.IT:
      return 'it';
    case ShoreMasterDepartment.Logistics:
      return 'logistics';
    case ShoreMasterDepartment.Marketing:
      return 'marketing';
    case ShoreMasterDepartment.Operations:
      return 'operations';
    case ShoreMasterDepartment.Sales:
      return 'sales';

    default:
      throw new Error(`Invalid DepartmentId. ${departmentId} has no coorelating permissionType.`);
  }
};
